import React, { useMemo, useEffect, useRef, useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useTunerStore, presetForId } from '../stores/tunerStore.js';
import { useAudioTuner } from '../hooks/useAudioTuner.js';
import { useTopBarStore } from '../stores/topBarStore.js';
import { CentMeter } from '../components/tuner/CentMeter.js';
import { StringPicker } from '../components/tuner/StringPicker.js';
import { Card } from '../components/ui/Card.js';
import { Button } from '../components/ui/Button.js';
import { Switch } from '../components/ui/Switch.js';
import { Check, Mic, MicOff, AlertCircle } from 'lucide-react';
import { TuningPicker } from '../components/tuning/TuningPicker.js';

export const TunerPage: React.FC = () => {
  usePageTitle('کوک‌کننده');
  const {
    isListening,
    selectedPreset,
    selectedString,
    detectedNote,
    detectedOctave,
    detectedFrequency,
    centsDeviation,
    referenceA4,
    errorMessage,
    setPreset,
    setSelectedString,
    setReferenceA4
  } = useTunerStore();

  const setTopBarConfig = useTopBarStore((s) => s.setTopBarConfig);
  const resetTopBarConfig = useTopBarStore((s) => s.resetTopBarConfig);

  const { startListening, stopListening } = useAudioTuner();

  // Configure mobile TopBar to match TunerMobile.png: Title «تیونر» + «A4 440» chip
  useEffect(() => {
    setTopBarConfig({
      title: 'تیونر',
      showLogo: false,
      hideDefaultActions: true,
      actions: (
        <span
          dir="ltr"
          className="h-8 px-2.5 border border-base-300 rounded-[10px] font-mono text-xs text-base-content/80 inline-flex items-center select-none"
        >
          A4 {referenceA4}
        </span>
      )
    });

    return () => {
      resetTopBarConfig();
    };
  }, [referenceA4, setTopBarConfig, resetTopBarConfig]);

  const handleToggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Only consider genuinely in-tune when listening, pitch is detected, and within ±3 cents
  const isActuallyInTune =
    isListening && !!detectedNote && Math.abs(centsDeviation) <= 3;

  // FND-20: polite live region. Announces a changed note or in-tune state, at most once per second.
  const [announcement, setAnnouncement] = useState('');
  const lastAnnounceRef = useRef({ key: '', at: 0 });
  const liveText =
    isListening && detectedNote
      ? `${detectedNote}${detectedOctave ?? ''}${isActuallyInTune ? '، کوک دقیق' : ''}`
      : '';
  useEffect(() => {
    if (!liveText) {
      lastAnnounceRef.current.key = '';
      setAnnouncement('');
      return;
    }
    if (liveText === lastAnnounceRef.current.key) return;
    const wait = Math.max(0, 1000 - (performance.now() - lastAnnounceRef.current.at));
    const timer = window.setTimeout(() => {
      lastAnnounceRef.current = { key: liveText, at: performance.now() };
      setAnnouncement(liveText);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [liveText]);

  const lowestNoteHz = Math.min(...selectedPreset.notes.map((n) => n.frequency));

  // Find detected string in current preset if in auto-detect mode
  const detectedStringIndex = useMemo(() => {
    if (!detectedNote) return null;
    const exact = selectedPreset.notes.find(
      (n) => n.note === detectedNote && n.octave === detectedOctave
    );
    if (exact) return exact.stringNumber;
    const noteMatch = selectedPreset.notes.find((n) => n.note === detectedNote);
    return noteMatch ? noteMatch.stringNumber : null;
  }, [selectedPreset.notes, detectedNote, detectedOctave]);

  return (
    <div className="w-full max-w-[880px] mx-auto -mt-[3px] md:mt-0 flex flex-col gap-4 md:gap-5 min-h-[calc(100dvh-56px-84px-40px)] lg:min-h-0 pb-32 pb-[calc(theme(spacing.24)+env(safe-area-inset-bottom))] md:pb-6">
      {/* Backward-compatible invisible header element for older test suites */}
      <h1 className="sr-only">تیونر کروماتیک گیتار</h1>
      <div role="status" aria-live="polite" aria-atomic="true" data-testid="tuner-live-region" className="sr-only">
        {announcement}
      </div>

      {/* Header bar matching Tuner.png (desktop) and TunerMobile.png (mobile) */}
      <header
        data-testid="tuner-page-header"
        className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 select-none"
      >
        {/* Desktop title & subtitle (hidden on mobile since TopBar displays it) */}
        <div className="hidden md:block">
          <h1 className="text-2xl font-bold text-base-content m-0">
            تیونر
          </h1>
          <p className="text-sm text-base-content/60 mt-1 mb-0">
            یک سیم را بنوازید؛ نت به‌صورت خودکار تشخیص داده می‌شود.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          {/* Tuning Preset Selector (full-width on mobile, auto on desktop) */}
          <div className="w-full md:w-[440px]">
            <TuningPicker
              value={selectedPreset.id}
              valueKey="id"
              onChange={(id) => {
                const preset = presetForId(id);
                if (preset) setPreset(preset);
              }}
              testIdPrefix="tuner-tuning"
              selectTestId="tuner-preset-select"
            />
            {lowestNoteHz < 45 && (
              <p data-testid="tuner-low-note-hint" className="mt-2 text-[11px] leading-5 text-warning">
                نت‌های خیلی بم روی میکروفون گوشی دقیق نیستند؛ برای نتیجه بهتر سیم را روی هارمونیک دوازدهم بزنید.
              </p>
            )}
          </div>

          {/* Desktop A4 Reference Frequency Stepper (hidden on mobile, chip is in TopBar) */}
          <div
            dir="ltr"
            className="hidden md:inline-flex h-10 items-center border border-base-300 rounded-xl bg-base-100 overflow-hidden"
          >
            <button
              type="button"
              data-testid="tuner-a4-decrement"
              aria-label="کاهش فرکانس مبنا"
              disabled={referenceA4 <= 430}
              onClick={() => setReferenceA4(Math.max(430, referenceA4 - 1))}
              className="w-9 h-full flex items-center justify-center font-mono text-base text-base-content/70 hover:bg-base-200 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              −
            </button>
            <span className="px-[5.5px] font-mono text-[13px] font-semibold text-base-content tabular-nums select-none">
              A4 = {referenceA4} Hz
            </span>
            <button
              type="button"
              data-testid="tuner-a4-increment"
              aria-label="افزایش فرکانس مبنا"
              disabled={referenceA4 >= 450}
              onClick={() => setReferenceA4(Math.min(450, referenceA4 + 1))}
              className="w-9 h-full flex items-center justify-center font-mono text-base text-base-content/70 hover:bg-base-200 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              +
            </button>
          </div>
        </div>
      </header>

      {/* Microphone Error Banner (FRE-200) */}
      {errorMessage && (
        <div
          role="alert"
          data-testid="tuner-mic-error-banner"
          className="w-full bg-error/10 border border-error/25 text-base-content rounded-2xl p-4 flex items-center justify-between gap-3 text-start animate-in fade-in duration-200"
        >
          <div className="flex items-center gap-3 min-w-0">
            <AlertCircle className="size-5 text-error shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-sm text-error mb-0.5">خطا در دسترسی به میکروفون</p>
              <p className="text-xs text-base-content/80 m-0 truncate md:whitespace-normal">{errorMessage}</p>
              <p className="text-[11px] text-base-content/70 mt-1 m-0">
                لطفاً از تنظیمات مرورگر، دسترسی میکروفون را فعال کرده و صفحه را دوباره بارگذاری کنید.
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={startListening}
            data-testid="tuner-mic-retry-button"
            className="shrink-0 text-xs"
          >
            تلاش مجدد
          </Button>
        </div>
      )}

      {/* Main Tuner Card */}
      <Card
        className={`p-5 md:p-10 flex flex-col items-center justify-center flex-1 lg:flex-none gap-5 md:gap-6 transition-all duration-300 rounded-3xl bg-base-100 ${
          isActuallyInTune
            ? 'border-success/45 shadow-tuner-glow'
            : 'border-base-300 shadow-none'
        }`}
      >
        {/* Dynamic Status Badge */}
        {isActuallyInTune ? (
          <span
            data-testid="tuner-status-badge"
            className="h-[30px] px-3.5 rounded-full bg-success/14 text-success-soft text-[13px] font-bold inline-flex items-center gap-2 select-none"
          >
            <Check className="w-3.5 h-3.5 stroke-[3]" />
            <span>کوک دقیق</span>
          </span>
        ) : isListening && detectedNote ? (
          centsDeviation < -3 ? (
            <span
              data-testid="tuner-status-badge"
              className="h-[30px] px-3.5 rounded-full bg-error/14 text-error-soft text-[13px] font-bold inline-flex items-center gap-2 select-none"
            >
              <span>کمی بم است · سیم را سفت کنید</span>
            </span>
          ) : (
            <span
              data-testid="tuner-status-badge"
              className="h-[30px] px-3.5 rounded-full bg-error/14 text-error-soft text-[13px] font-bold inline-flex items-center gap-2 select-none"
            >
              <span>کمی زیر است · سیم را شل کنید</span>
            </span>
          )
        ) : isListening ? (
          <span
            data-testid="tuner-status-badge"
            className="h-[30px] px-3.5 rounded-full bg-base-300/60 text-base-content/60 text-[13px] font-bold inline-flex items-center gap-2 select-none"
          >
            <span>یک سیم را بنوازید</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={handleToggleListening}
            data-testid="tuner-status-badge"
            className="h-11 px-4 rounded-full bg-primary-fill text-primary-content hover:bg-primary-focus active:scale-95 text-xs md:text-[13px] font-bold inline-flex items-center gap-2 select-none shadow-md transition cursor-pointer"
          >
            <Mic className="size-4 md:size-5 shrink-0" />
            <span>برای شروع · شروع کوک</span>
          </button>
        )}

        {/* Giant Note Readout */}
        <div dir="ltr" className="flex items-start justify-center gap-1 leading-none select-none">
          <span
            data-testid="tuner-note-letter"
            className={`font-mono text-[112px] md:text-[136px] font-bold leading-none tracking-tight transition-colors duration-150 ${
              isActuallyInTune
                ? 'text-success-soft'
                : isListening && detectedNote
                ? 'text-base-content'
                : 'text-base-content/40 font-light'
            }`}
          >
            {isListening && detectedNote ? detectedNote : '—'}
          </span>
          {isListening && detectedOctave !== null && (
            <span
              data-testid="tuner-octave"
              className="font-mono text-[26px] md:text-[32px] font-semibold text-base-content/60 mt-3 md:mt-4"
            >
              {detectedOctave}
            </span>
          )}
        </div>

        {/* Pitch & Deviation Readout */}
        <p dir="ltr" className="m-0 font-mono text-base md:text-lg text-base-content/80 tabular-nums">
          {isListening && detectedFrequency > 0 ? detectedFrequency.toFixed(2) : '—'} Hz ·{' '}
          <span
            className={
              isActuallyInTune
                ? 'text-success-soft font-semibold'
                : isListening && detectedNote && centsDeviation !== 0
                ? 'text-error-soft font-semibold'
                : 'text-base-content/80'
            }
          >
            {isListening && detectedNote
              ? centsDeviation > 0
                ? `+${centsDeviation.toFixed(1)}¢`
                : `${centsDeviation.toFixed(1)}¢`
              : '—'}
          </span>
        </p>

        {/* Cent Meter Viewport */}
        <CentMeter
          cents={centsDeviation}
          isInTune={isActuallyInTune}
          isListening={isListening}
        />

        {/* String Picker Buttons */}
        <StringPicker
          notes={selectedPreset.notes}
          selectedString={selectedString}
          detectedString={detectedStringIndex}
          isInTune={isActuallyInTune}
          onSelectString={setSelectedString}
        />

        {/* Auto-detect Switch (hidden on mobile per design reference) */}
        <div className="hidden md:flex items-center gap-2.5 text-[13px] text-base-content/70">
          <Switch
            data-testid="tuner-auto-detect-switch"
            id="tuner-auto-detect"
            label="تشخیص خودکار سیم"
            tone="primary"
            labelPosition="end"
            labelClassName="text-[13px] text-base-content/60"
            className="!gap-2.5"
            checked={selectedString === null}
            onCheckedChange={(checked) => {
              setSelectedString(checked ? null : selectedPreset.notes[0].stringNumber);
            }}
          />
        </div>
      </Card>

      {/* Microphone Control Bar (responsive: full-width 52px on mobile, flex bar on desktop) */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-base-100 border border-base-300 rounded-2xl p-0 md:p-4">
        {/* Desktop status label */}
        <div className="hidden md:flex items-center gap-2.5">
          <span
            className={`size-2.5 rounded-full ${
              isListening
                ? 'bg-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.2)] animate-pulse'
                : 'bg-base-content/40'
            }`}
            aria-hidden="true"
          />
          <span className="text-sm font-medium text-base-content">
            {isListening ? 'میکروفون فعال است' : 'میکروفون خاموش است'}
          </span>
        </div>

        {/* Toggle Button */}
        <Button
          data-testid="tuner-mic-toggle"
          type="button"
          variant={isListening ? 'secondary' : 'primary'}
          size="md"
          onClick={handleToggleListening}
          className="w-full md:w-auto h-[52px] md:h-10 px-4 text-sm font-semibold rounded-2xl md:rounded-xl"
          leadingIcon={
            isListening ? (
              <>
                {/* Mobile status indicator dot inside button */}
                <span
                  className="md:hidden size-2.5 shrink-0 rounded-full bg-red-500"
                  aria-hidden="true"
                />
                <MicOff className="hidden md:inline size-4 shrink-0" />
              </>
            ) : (
              <Mic className="size-5 shrink-0" />
            )
          }
        >
          {isListening ? 'توقف میکروفون' : 'فعال‌سازی میکروفون و شروع کوک'}
        </Button>
      </div>
    </div>
  );
};

TunerPage.displayName = 'TunerPage';
